import { Suspense, useCallback, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useSearchParams } from 'react-router'
import { usePageConfig } from '@hooks/usePageConfig'
import { useTableHighlight } from '@hooks/useTableHighlight'
import type { ResourceScope } from 'src/types/global'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage'
import Loader from '@components/Loader/Loader'
import Summary from '@components/Summary/Summary'
import QueryList from './QueryList/QueryList'
import ExternalHeaderContent from '@components/ExternalHeaderContent/ExternalHeaderContent'
import HeroBanner from '@components/HeroBanner/HeroBanner'
import FilterSelector from '@components/FilterSelector/FilterSelector'
import SortSelector from '@components/SortSelector/SortSelector'
import ItemListSkeleton from '@components/skeletons/ItemListSkeleton/ItemListSkeleton'
import Tabs from '@components/Tabs/Tabs'
import { useQueryListData } from '@hooks/useQueryListData'

import './Queries.css'

const QueriesSummary = () => {
  const yours = useQueryListData('own')
  const total = useQueryListData('public')
  return (
    <Summary
      items={[
        { label: 'yours', value: yours.length },
        { label: 'total', value: total.length }
      ]}
      id="queries"
    />
  )
}

export default function Queries() {
  const intl = useIntl()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const gridRef = useRef<HTMLDivElement>(null)

  const currentScope: ResourceScope =
    searchParams.get('scope') === 'public' ? 'public' : 'own'

  useTableHighlight(gridRef, searchQuery)

  const headerContent = useMemo(() => {
    const crumbs = [
      {
        label: intl.formatMessage({ id: 'nav.queries' }),
        path: '/queries'
      }
    ]
    return (
      <ExternalHeaderContent
        hasBack
        crumbs={crumbs}
        headerActions={
          <Link className="highlight-button button" to="/query-builder/new">
            <span className="icon icon-code-stroke" />
            <span>
              <FormattedMessage id="new-query" />
            </span>
          </Link>
        }
      />
    )
  }, [intl])

  usePageConfig('queries', headerContent)

  const tabs = useMemo(
    () => [
      { id: 'own', label: intl.formatMessage({ id: 'tab.own-queries' }) },
      { id: 'public', label: intl.formatMessage({ id: 'tab.public' }) }
    ],
    [intl]
  )

  const handleTabChange = useCallback(
    (id: string) => {
      setSearchParams((prev) => {
        const sort = prev.get('sort')
        const next = new URLSearchParams()
        if (id === 'public') next.set('scope', 'public')
        if (sort) next.set('sort', sort)
        return next
      })
    },
    [setSearchParams]
  )

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value)
    },
    []
  )

  return (
    <section id="queries-content" className="main-content">
      <HeroBanner nameId="queries">
        <ErrorBoundary errorComponent={ErrorMessage}>
          <Suspense fallback={<Loader />}>
            <QueriesSummary />
          </Suspense>
        </ErrorBoundary>
      </HeroBanner>
      <div id="queries-display">
        <div className="container">
          <Tabs
            tabs={tabs}
            activeTab={currentScope}
            onChange={handleTabChange}
          />
        </div>
        <header>
          <div className="container">
            <search className="search-highlight">
              <input
                type="search"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={intl.formatMessage({
                  id: 'search-data.placeholder'
                })}
              />
            </search>
            <div className="selectors-wrapper">
              {currentScope !== 'public' && <FilterSelector />}
              <SortSelector />
            </div>
          </div>
        </header>
        <div id="queries-grid" ref={gridRef}>
          <div className="container">
            <ErrorBoundary errorComponent={ErrorMessage}>
              <Suspense fallback={<ItemListSkeleton />}>
                <QueryList scope={currentScope} />
              </Suspense>
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </section>
  )
}
